import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import Logo from "@/components/Logo";
import { useLogoDesign } from "@/hooks/useLogoDesign";
import { Menu, User, LogOut, Heart, MessageSquare, LayoutDashboard, X, Sparkles } from "lucide-react";

export default function Header() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [hasPartner, setHasPartner] = useState(false);
  const navigate = useNavigate();
  const logoDesign = useLogoDesign();

  useEffect(() => {
    if (!user) { setHasPartner(false); return; }
    api.get("/me/concierge/profile").then(() => setHasPartner(true)).catch(() => setHasPartner(false));
  }, [user]);

  const handleLogout = async () => {
    await logout();
    setOpen(false);
    navigate("/");
  };

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-white/85 border-b border-black/10"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16 sm:h-20">
        <Link to="/" data-testid="logo-link" className="flex items-center group">
          <Logo design={logoDesign} size={40} />
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          <NavLink
            to="/search"
            data-testid="nav-search"
            className={({ isActive }) =>
              `text-sm font-semibold hover:text-[#1B2A49] transition-colors ${isActive ? "text-[#1B2A49]" : "text-neutral-900"}`
            }
          >
            Découvrir
          </NavLink>
          <NavLink
            to="/become-host"
            data-testid="nav-host"
            className="text-sm font-semibold text-neutral-900 hover:text-[#1B2A49] transition-colors"
          >
            Mettre en location
          </NavLink>
          <NavLink
            to="/partenaires"
            data-testid="nav-partners"
            className="text-sm font-semibold text-neutral-900 hover:text-[#1B2A49] transition-colors"
          >
            Partenaires
          </NavLink>
          <NavLink
            to="/support"
            data-testid="nav-support"
            className="text-sm font-semibold text-neutral-900 hover:text-[#1B2A49] transition-colors"
          >
            Support
          </NavLink>
          {user ? (
            <div className="flex items-center gap-3">
              {user.role === "admin" && (
                <Link to="/admin" data-testid="nav-admin" className="text-sm font-semibold flex items-center gap-2 bg-[#7A1535] text-white px-3 py-2 hover:bg-[#5B0F26] transition-colors">
                  Admin
                </Link>
              )}
              {hasPartner && (
                <Link to="/conciergerie" data-testid="nav-concierge" className="text-sm font-semibold flex items-center gap-2 hover:text-[#1B2A49] transition-colors">
                  <Sparkles className="w-4 h-4" /> Conciergerie
                </Link>
              )}
              <Link
                to={user.role === "owner" || user.role === "admin" ? "/owner" : "/traveler"}
                data-testid="nav-dashboard"
                className="text-sm font-semibold hover:text-[#1B2A49] transition-colors flex items-center gap-2"
              >
                <LayoutDashboard className="w-4 h-4" /> Espace
              </Link>
              <Link to="/wishlists" data-testid="nav-wishlists" className="text-sm font-semibold flex items-center gap-2 hover:text-[#7A1535]">
                <Heart className="w-4 h-4" /> Listes
              </Link>
              <Link to="/messages" data-testid="nav-messages" className="text-sm font-semibold flex items-center gap-2 hover:text-[#1B2A49]">
                <MessageSquare className="w-4 h-4" /> Messages
              </Link>
              <button
                data-testid="nav-logout"
                onClick={handleLogout}
                className="text-sm font-semibold flex items-center gap-2 px-4 py-2 bg-[#0A0A0A] text-white hover:bg-[#1B2A49] transition-colors"
              >
                <LogOut className="w-4 h-4" /> Déconnexion
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Link
                to="/login"
                data-testid="nav-login"
                className="text-sm font-semibold px-4 py-2 hover:bg-neutral-100 transition-colors"
              >
                Se connecter
              </Link>
              <Link
                to="/register"
                data-testid="nav-register"
                className="text-sm font-semibold px-4 py-2 bg-[#0A0A0A] text-white hover:bg-[#1B2A49] transition-colors"
              >
                S'inscrire
              </Link>
            </div>
          )}
        </nav>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen(!open)}
          className="md:hidden p-2"
          aria-label="Menu"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-black/10 bg-white">
          <div className="px-4 py-4 flex flex-col gap-3">
            <Link to="/search" data-testid="m-nav-search" onClick={() => setOpen(false)} className="text-sm font-semibold py-2">Découvrir</Link>
            <Link to="/become-host" data-testid="m-nav-host" onClick={() => setOpen(false)} className="text-sm font-semibold py-2">Mettre en location</Link>
            <Link to="/partenaires" data-testid="m-nav-partners" onClick={() => setOpen(false)} className="text-sm font-semibold py-2">Partenaires</Link>
            <Link to="/support" data-testid="m-nav-support" onClick={() => setOpen(false)} className="text-sm font-semibold py-2">Support</Link>
            {user ? (
              <>
                {user.role === "admin" && (
                  <Link to="/admin" data-testid="m-nav-admin" onClick={() => setOpen(false)} className="text-sm font-semibold py-2 bg-[#7A1535] text-white px-3 inline-block">Admin</Link>
                )}
                {hasPartner && (
                  <Link to="/conciergerie" data-testid="m-nav-concierge" onClick={() => setOpen(false)} className="text-sm font-semibold py-2 flex items-center gap-2">
                    <Sparkles className="w-4 h-4" /> Mon espace conciergerie
                  </Link>
                )}
                <Link
                  to={user.role === "owner" || user.role === "admin" ? "/owner" : "/traveler"}
                  onClick={() => setOpen(false)}
                  data-testid="m-nav-dashboard"
                  className="text-sm font-semibold py-2"
                >
                  Espace
                </Link>
                <Link to="/messages" data-testid="m-nav-messages" onClick={() => setOpen(false)} className="text-sm font-semibold py-2">Messages</Link>
                <button data-testid="m-nav-logout" onClick={handleLogout} className="text-left text-sm font-semibold py-2">Déconnexion</button>
              </>
            ) : (
              <>
                <Link to="/login" data-testid="m-nav-login" onClick={() => setOpen(false)} className="text-sm font-semibold py-2">Se connecter</Link>
                <Link to="/register" data-testid="m-nav-register" onClick={() => setOpen(false)} className="text-sm font-semibold py-2 bg-[#0A0A0A] text-white px-4 inline-block">S'inscrire</Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
